// @flow
import { Fragment, useMemo, type Node } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Link } from '@tablekit/typography';
import GatsbyLink from 'gatsby-link';
import { safeLink } from 'utils/safeLink';
import type { DocType, NestedDocType } from 'components/types';
import * as componentKeys from 'componentKeys';
import {
  RichTextWrapper,
  ConversationSpeaker,
  Heading,
  markStyles
} from './styled';
import TemplateParagraph from './TemplateParagraph';
import { imageTransform } from '../Image';

const StoryblokLink = Link.withComponent(GatsbyLink);

function renderBlok(node) {
  const blok = node.attrs?.body[0];
  if (!blok) return null;
  switch (blok.component) {
    case componentKeys.BLOG_CONVERSATION_SPEAKER:
      return (
        <p>
          <ConversationSpeaker>{blok.name}</ConversationSpeaker> {blok.content}
        </p>
      );
    case componentKeys.TABLE:
      return (
        <table
          dangerouslySetInnerHTML={{
            __html: `<thead><tr>${blok.content.thead
              .map(({ value }) => `<th>${value}</th>`)
              .join('')}</tr></thead>
          <tbody>${blok.content.tbody
            .map(
              ({ body }) =>
                `<tr>${body
                  .map(({ value }) => `<td>${value}</td>`)
                  .join('')}</tr>`
            )
            .join('')}</tbody>`
          }}
        />
      );
    case componentKeys.TEMPLATE_PARAGRAPH:
      return <TemplateParagraph blok={blok} />;
    default:
      return null;
  }
}

const nodeComponents = {
  horizontal_rule: styled.hr``,
  blockquote: styled.blockquote``,
  bullet_list: styled.ul``,
  hard_break: styled.br``,
  heading: ({ level, ...props }: any) => (
    <Heading as={`h${level || 2}`} {...props} />
  ),
  image: ({ src, alt, title }: any) => (
    <img src={imageTransform(src)} alt={alt} title={title} />
  ),
  list_item: styled.li``,
  ordered_list: styled.ol``,
  paragraph: styled.p``
};

export function renderText(node?: NestedDocType) {
  if (!node) return '';
  const { text, marks } = node;
  if (!marks || marks.length === 0) return text;
  let Component = 'span';
  const styles = [];
  const attributes = {};
  for (let i = 0; i < marks.length; i += 1) {
    const { attrs = {}, type } = marks[i];
    // $FlowFixMe I don't care that this not all keys are defined
    const markStyle = markStyles[type];
    if (markStyle) {
      styles.push(markStyle);
    } else if (type === 'styled') {
      attributes.className = attrs.class;
    } else if (type === 'link') {
      Component = Link;
      const { linktype = 'url' } = attrs;
      if (linktype === 'email') {
        attributes.href = `mailto:${attrs.href}`;
      } else if (linktype === 'story') {
        Component = StoryblokLink;
        const { full_slug: fullSlug, url } = attrs.story || {};
        attributes.to = safeLink(fullSlug || url);
      } else if (linktype === 'url' || attrs.href) {
        attributes.href = attrs.href;
        attributes.target = attrs.target;
        // This is a fallback case for https://github.com/storyblok/storyblok/issues/187
        if (
          attributes.href.indexOf('mailto:') !== 0 &&
          attributes.href.indexOf('@') !== -1
        ) {
          attributes.href = `mailto:${attributes.href}`;
        } else if (attrs.anchor) {
          attributes.href = `${attrs.href}#${attrs.anchor}`;
        }
      }
    }
  }
  return (
    <Component
      css={css`
        ${styles.join('\n')}
      `}
      {...attributes}
    >
      {text}
    </Component>
  );
}

function renderNode(node?: NestedDocType, index: number): Node {
  if (!node) return null;
  if (node.type === 'text') {
    return <Fragment key={index}>{renderText(node)}</Fragment>;
  }
  if (node.type === 'blok') {
    return <Fragment key={index}>{renderBlok(node)}</Fragment>;
  }
  const Component = nodeComponents[node.type];
  if (!Component) {
    console.warn(`Rich Text Node ${node.type} is not supported.`);
    return null;
  }
  let className;
  if (node.marks && node.marks[0] && node.marks[0].type === 'styled') {
    className = node.marks[0].attrs.class;
  }
  let content = null;
  if (node.content) {
    content = node.content.map(renderNode);
  }
  return (
    <Component key={index} className={className} {...(node.attrs || {})}>
      {content}
    </Component>
  );
}

export default function RichTextRenderer({
  richText,
  className
}: {
  className?: string,
  richText?: DocType
}): Node {
  const content = useMemo(() => {
    if (!richText || !richText.content) return null;
    return richText.content.map(renderNode);
  }, [richText]);
  return <RichTextWrapper className={className}>{content}</RichTextWrapper>;
}
